import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import JsSIP from 'jssip';
import logo from '../../../styles/logo.png';

const getTimestamp = () => {
  const now = new Date();
  return now.toLocaleTimeString('zh-TW', { hour12: false }) + '.' + String(now.getMilliseconds()).padStart(3, '0');
};

const VisitorNavbar = ({ visitorData, onSucess }) => {
  const { t } = useTranslation();
  const [status, setStatus] = useState('Not Connected');
  const originalSendRef = useRef(null);

  const injectSdpPatch = (uaInstance) => {
    if (!originalSendRef.current && uaInstance?.transport) {
      originalSendRef.current = uaInstance.transport.send;
      uaInstance.transport.send = function (...args) {
        if (args[0]?.body) {
          let body = args[0].body;

          const serverIP = process.env.REACT_APP_SIP_SERVER_IP || '35.221.190.216';

          if (body.includes('127.0.0.1') || body.includes('0.0.0.0')) {
            console.log('🔧 [SDP] 檢測到 localhost IP，替換為:', serverIP, '| 時間:', getTimestamp());
            body = body.replace(/IN IP4 (127\.0\.0\.1|0\.0\.0\.0)/g, `IN IP4 ${serverIP}`);
            body = body.replace(/c=IN IP4 (127\.0\.0\.1|0\.0\.0\.0)/g, `c=IN IP4 ${serverIP}`);
            args[0].body = body;
          }
        }
        return originalSendRef.current.apply(this, args);
      };
    }
  };

  useEffect(() => {
    if (!visitorData?.sipAccount) return;
    console.log('🔌 [SIP] 開始初始化 UA | 時間:' + getTimestamp());

    const socket = new JsSIP.WebSocketInterface(
      `wss://${visitorData?.sipAccountData?.domain ?? 'im.qrtalkie.com'}:8089/ws`
    );

    const uaInstance = new JsSIP.UA({
      sockets: [socket],
      uri: `sip:${visitorData.sipAccount}@${visitorData?.sipAccountData?.domain ?? 'im.qrtalkie.com'}`,
      password: visitorData.sipAccountData.password,
      register: true
    });

    uaInstance.on('registered', () => {
      console.log('✅ [SIP] 註冊成功 | UA:', uaInstance.configuration.uri, '| 時間:' + getTimestamp());
      setStatus(t('Ready'));
      injectSdpPatch(uaInstance);
    });

    uaInstance.on('unregistered', () => {
      console.log('❌ [SIP] 註銷 | 時間:' + getTimestamp());
      setStatus(t('Disconnected'));
    });
    uaInstance.on('registrationFailed', () => {
      console.log('❌ [SIP] 註冊失敗 | 時間:' + getTimestamp());
      setStatus(t('Registration Failed'));
    });

    uaInstance.start();
    onSucess(uaInstance);
    return () => {
      console.log('🛑 [SIP] UA 停止 | 時間:' + getTimestamp());
      uaInstance.stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visitorData?.sipAccount]);

  useEffect(() => {
    const isMobileDevice = () => {
      const ua = navigator.userAgent || navigator.vendor || window.opera;
      return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
    };

    const loadVConsole = () => {
      return new Promise((resolve) => {
        if (!isMobileDevice()) {
          console.log('🖥️ [調試] 桌面端瀏覽器，跳過 vConsole 加載 | 時間:' + getTimestamp());
          resolve(null);
          return;
        }

        if (window.vConsoleInstance) {
          resolve(window.vConsoleInstance);
          return;
        }
        const script = document.createElement('script');
        script.src = 'https://unpkg.com/vconsole@latest/dist/vconsole.min.js';
        script.async = false;
        script.onload = () => {
          window.vConsoleInstance = new window.VConsole({ maxLogNumber: 10000 });
          console.log('📱 [調試] vConsole 加載完成（移動端）| 時間:' + getTimestamp());
          resolve(window.vConsoleInstance);
        };
        script.onerror = () => {
          console.error('❌ [調試] vConsole 腳本加載失敗 | 時間:' + getTimestamp());
          resolve(null);
        };
        document.head.appendChild(script);
      });
    };

    const enableJsSIPDebug = () => {
      const wait = () => {
        if (isMobileDevice() && !window.vConsoleInstance) {
          setTimeout(wait, 100); return;
        }
        localStorage.setItem('debug', 'JsSIP:*');
        if (JsSIP.debug?.enable) JsSIP.debug.enable('JsSIP:*');
        console.log('✅ [調試] JsSIP 調試已啟用 | 時間:' + getTimestamp());
      };
      wait();
    };

    const interceptWS = () => {
      if (window._wsDone) return;
      window._wsDone = true;
      const OriginalWS = window.WebSocket;
      window.WebSocket = function(url, protocols) {
        const ws = new OriginalWS(url, protocols);
        console.log(`[🔌 WS] 連接：${url} | 時間:` + getTimestamp());

        ws.addEventListener('open', () => {
          console.log('✅ [WS] 連接已建立 | 時間:' + getTimestamp());
        });
        ws.addEventListener('close', () => {
          console.log('🔴 [WS] 連接已關閉 | 時間:' + getTimestamp());
        });
        ws.addEventListener('error', (e) => {
          console.log('❌ [WS] 連接錯誤:', e, '| 時間:' + getTimestamp());
        });

        ws.addEventListener('message', e => {
          const recvTime = getTimestamp();
          const d = typeof e.data === 'string' ? (e.data.length > 800 ? e.data.slice(0,800)+'...（省略）' : e.data) : '[Binary]';
          console.log('[📥 WS 接收]', d, '| 時間:', recvTime);

          // 🔵 新增：檢測 401 響應
          if (typeof e.data === 'string' && e.data.includes('401')) {
            console.log('🔐 [SIP] 收到 401 Unauthorized | 時間:', recvTime);
          }
          if (typeof e.data === 'string' && e.data.includes('180')) {
            console.log('📞 [SIP] 收到 180 Ringing | 時間:', recvTime);
          }
        });
        const origSend = ws.send;
        ws.send = function(data) {
          const sendTime = getTimestamp();
          const bufferedAmount = this.bufferedAmount;
          const readyState = this.readyState;
          const msgPreview = typeof data === 'string' ? (data.length > 200 ? data.slice(0,200)+'...' : data) : '[Binary]';
          console.log('[📤 WS 發送]', msgPreview, '| 時間:', sendTime);
          console.log('[📤 WS 發送] 緩衝區大小:', bufferedAmount);
          console.log('[📤 WS 發送] readyState:', readyState);

          // 🔵 新增：檢測 INVITE 消息
          if (typeof data === 'string' && data.includes('INVITE')) {
            console.log('📤 [SIP] 發送 INVITE | 時間:', sendTime);
            const callIdMatch = data.match(/Call-ID:\s*(\S+)/);
            if (callIdMatch) {
              console.log('📤 [SIP] Call-ID:', callIdMatch[1]);
            }
          }

          return origSend.call(this, data);
        };
        return ws;
      };
      console.log('✅ [調試] WebSocket 攔截已啟用 | 時間:' + getTimestamp());
    };

    (async () => {
      console.log('=== 🛠️ 調試模塊初始化開始 | 時間:' + getTimestamp() + ' ===');
      await loadVConsole();
      if (isMobileDevice()) {
        if (window.vConsoleInstance) {
          console.log('✅ [調試] 移動端：vConsole 已就緒 | 時間:' + getTimestamp());
        } else {
          console.log('⚠️ [調試] 移動端：vConsole 加載失敗 | 時間:' + getTimestamp());
        }
      } else {
        console.log('🖥️ [調試] 桌面端：使用原生 Console (F12) | 時間:' + getTimestamp());
      }
      enableJsSIPDebug();
      interceptWS();
      console.log('=== 🛠️ 調試模塊初始化完成 | 時間:' + getTimestamp() + ' ===');
    })();
  }, []);

  return (
    <div className="h-[50px] bg-black flex items-center justify-between px-4 text-white">
      <div className="flex items-center">
        <img src={logo} className="w-8 h-8" alt="logo" />
        <span className="text-white ml-2">QRTalkie</span>
      </div>
      <div className="flex items-center text-white ml-auto">
        <span>{t('Status')}:</span>
        <div className="flex items-center ml-2">
          <div
            className={`w-2 h-2 rounded-full mr-2 ${
              status === t('Ready')
                ? 'bg-green-500'
                : status === t('Connecting...')
                  ? 'bg-yellow-500'
                  : 'bg-red-500'
            }`}
          />
          <span>{status}</span>
        </div>
      </div>
    </div>
  );
};

export default VisitorNavbar;
